import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Input, Select } from '@monigo/ui';
import { BeneficiarySchema, type BeneficiaryInput } from '@monigo/shared';
import { useBeneficiaries, useCreateBeneficiary, useUpdateBeneficiary } from './api.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';

export function BeneficiaryFormPage() {
  const { t } = useTranslation();
  const translateError = useCallback(
    (key?: string) => {
      if (!key) return undefined;
      const localeKey = key
        .replace('beneficiary.fullName.required', 'beneficiaries.errors.fullNameRequired')
        .replace('beneficiary.phone.invalid', 'beneficiaries.errors.phone')
        .replace('beneficiary.bankName.required', 'beneficiaries.errors.bankNameRequired')
        .replace('beneficiary.iban.invalid', 'beneficiaries.errors.iban')
        .replace('beneficiary.holderName.required', 'beneficiaries.errors.holderNameRequired');
      return t(localeKey, key);
    },
    [t],
  );
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const create = useCreateBeneficiary();
  const update = useUpdateBeneficiary(params.id ?? '');
  const beneficiariesQ = useBeneficiaries();

  const editingBeneficiary = params.id
    ? beneficiariesQ.data?.items.find((b) => b.id === params.id)
    : undefined;

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<BeneficiaryInput>({
    resolver: zodResolver(BeneficiarySchema),
    defaultValues: {
      country: 'CM',
      payout_method: 'mobile_money',
      payout_details: { provider: 'mtn_momo', phone_number: '' } as never,
    },
  });

  // Pre-fill form when editing
  useEffect(() => {
    if (editingBeneficiary) {
      const details = editingBeneficiary.payout_details as Record<string, unknown>;
      reset({
        full_name: editingBeneficiary.full_name,
        country: editingBeneficiary.country as 'CM',
        payout_method: editingBeneficiary.payout_method,
        payout_details: details as never,
      });
    }
  }, [editingBeneficiary, reset]);

  const method = watch('payout_method');

  if (params.id && beneficiariesQ.isLoading) return <LoadingScreen />;

  const onSubmit: SubmitHandler<BeneficiaryInput> = async (values) => {
    if (params.id) {
      await update.mutateAsync(values);
    } else {
      await create.mutateAsync(values);
    }
    navigate('/beneficiaries');
  };

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <Link
        to="/beneficiaries"
        className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-elevated text-2xl text-text"
      >
        ←
      </Link>

      <div className="space-y-2">
        <h1 className="text-5xl font-semibold tracking-tight text-primary">
          {params.id ? 'Edit recipient' : 'Add recipient'}
        </h1>
        <p className="text-2xl text-text-muted">Choose the delivery method</p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <Field
          label={t('beneficiaries.fullName')}
          error={translateError(errors.full_name?.message)}
        >
          <Input
            hasError={!!errors.full_name}
            placeholder="Recipient name"
            {...register('full_name')}
          />
        </Field>

        <Field label={t('beneficiaries.payoutMethod')}>
          <div className="space-y-4">
            <MethodOption
              active={method === 'mobile_money'}
              onClick={() => setValue('payout_method', 'mobile_money')}
              title={t('beneficiaries.mobileMoney')}
              icon="/MOBILE_ICON.png"
            />
            <MethodOption
              active={method === 'bank'}
              onClick={() => setValue('payout_method', 'bank')}
              title={t('beneficiaries.bank')}
              icon="/BANK_ICON.png"
            />
          </div>
        </Field>

        <input type="hidden" {...register('payout_method')} />

        {method === 'mobile_money' ? (
          <MobileMoneyFields
            register={register}
            setValue={setValue}
            errors={errors}
            translateError={translateError}
          />
        ) : null}
        {method === 'bank' ? (
          <BankFields register={register} errors={errors} translateError={translateError} />
        ) : null}

        <Button
          type="submit"
          isLoading={isSubmitting || create.isPending || update.isPending}
          className="h-16 w-full text-2xl font-medium"
        >
          {params.id ? 'Save recipient' : 'Continue'}
        </Button>
      </form>
    </div>
  );
}

type Reg = ReturnType<typeof useForm<BeneficiaryInput>>['register'];
type Errs = ReturnType<typeof useForm<BeneficiaryInput>>['formState']['errors'];

function MobileMoneyFields({
  register,
  setValue,
  errors,
  translateError,
}: {
  register: Reg;
  setValue: (name: 'payout_details.provider', value: 'mtn_momo' | 'orange_money') => void;
  errors: Errs;
  translateError: (key?: string) => string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const providerOptions = [
    { value: 'mtn_momo', label: t('beneficiaries.providerMtn') },
    { value: 'orange_money', label: t('beneficiaries.providerOrange') },
  ];
  const [provider, setProvider] = useState<'mtn_momo' | 'orange_money'>('mtn_momo');

  return (
    <div className="space-y-5">
      <Field label={t('beneficiaries.provider')}>
        <div className="flex items-center gap-3">
          <Select
            value={provider}
            onChange={(v) => {
              const p = v as 'mtn_momo' | 'orange_money';
              setProvider(p);
              setValue('payout_details.provider', p);
            }}
            options={providerOptions}
            className="min-w-40"
          />
          <img
            src={provider === 'mtn_momo' ? '/MTN_ICON.png' : '/ORANGE_ICON.png'}
            alt=""
            className="h-10 w-10 rounded-[10px] object-contain"
          />
        </div>
      </Field>
      <Field
        label={t('beneficiaries.phone')}
        error={translateError(
          (errors as { payout_details?: { phone_number?: { message?: string } } })?.payout_details
            ?.phone_number?.message,
        )}
      >
        <Input
          placeholder="+237 6XX XX XX XX"
          {...register('payout_details.phone_number' as never)}
        />
      </Field>
    </div>
  );
}

function BankFields({
  register,
  errors,
  translateError,
}: {
  register: Reg;
  errors: Errs;
  translateError: (key?: string) => string | undefined;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <Field label={t('beneficiaries.bankName')}>
        <Input {...register('payout_details.bank_name' as never)} />
      </Field>
      <Field
        label={t('beneficiaries.iban')}
        error={translateError(
          (errors as { payout_details?: { iban?: { message?: string } } })?.payout_details?.iban
            ?.message,
        )}
      >
        <Input placeholder="CM21 …" {...register('payout_details.iban' as never)} />
      </Field>
      <Field label={t('beneficiaries.holderName')}>
        <Input {...register('payout_details.account_holder_name' as never)} />
      </Field>
    </div>
  );
}

function MethodOption({
  active,
  onClick,
  title,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  icon: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-6 rounded-[1.5rem] border px-5 py-5 text-left ${
        active ? 'border-primary bg-surface-elevated' : 'border-border'
      }`}
    >
      <div className="flex h-16 w-20 items-center justify-center rounded-[1rem] border border-border bg-surface">
        <img src={icon} alt="" className="h-10 w-10 object-contain" />
      </div>
      <span className="text-3xl font-medium text-text">{title}</span>
    </button>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-3 text-sm">
      <span className="text-xl font-medium text-primary">{label}</span>
      {children}
      {error ? <span className="text-sm text-danger">{error}</span> : null}
    </label>
  );
}
